// 📄 components/abap/TransactionCatalogGrid.tsx
// Grid filtrable de tarjetas del Catálogo de Transacciones SAP — componente
// compartido entre la sección de Admin (CRUD completo, ver
// TransactionCatalogSection.tsx) y las vistas de solo lectura (Herramientas
// del proyecto y Ariba Toolbox): la UI de consulta/filtro/copiar es
// idéntica en todos lados, solo cambia si se muestran los botones de
// Editar/Eliminar. Mismo patrón que [[BadiCatalogGrid.tsx]].
import { useMemo, useState } from 'react';
import { Search, Copy, Check, Pencil, Trash2 } from 'lucide-react';
import {
  TransaccionSap,
  CATEGORIAS_TRANSACCION,
  SOLUCIONES_TRANSACCION,
  categoriaTransaccionMeta,
  solucionTransaccionMeta
} from '../../services/catalogoTransaccionesService.ts';

// Item del grid: TransaccionSap (catálogo global) con los campos extra que
// solo trae un desarrollo Z (ver transaccionZService.ts) — todos opcionales
// a propósito, así los 3 consumidores del catálogo global (Admin, Toolbox,
// esta misma vista) siguen pasando TransaccionSap[] tal cual sin adaptar
// nada. El llamador que muestra Z (TransaccionesCatalogo.tsx) arma este
// shape mapeando TransaccionZ.codigo -> transaccion.
type TransactionCatalogItem = TransaccionSap & {
  responsable?: string;
  sistemasDisponibles?: string[];
  rolPermiso?: string;
};

interface TransactionCatalogGridProps {
  transacciones: TransactionCatalogItem[];
  // true = catálogo de desarrollos Z de un proyecto: agrega el badge "Z" y
  // el bloque de responsable/sistemas/rol bajo la descripción de cada card.
  esZ?: boolean;
  readOnly?: boolean;
  onEdit?: (transaccion: TransaccionSap) => void;
  onDelete?: (transaccion: TransaccionSap) => void;
}

export default function TransactionCatalogGrid({ transacciones, esZ = false, readOnly = false, onEdit, onDelete }: TransactionCatalogGridProps) {
  const [busqueda, setBusqueda] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroSolucion, setFiltroSolucion] = useState('');
  const [copiadoId, setCopiadoId] = useState<string | null>(null);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return transacciones.filter((t) => {
      if (filtroCategoria && t.categoria !== filtroCategoria) return false;
      if (filtroSolucion && t.solucion !== filtroSolucion) return false;
      if (!q) return true;
      return [t.transaccion, t.nombre, t.descripcion].join(' ').toLowerCase().includes(q);
    });
  }, [transacciones, busqueda, filtroCategoria, filtroSolucion]);

  const handleCopiar = async (t: TransaccionSap) => {
    try {
      await navigator.clipboard.writeText(t.transaccion);
      setCopiadoId(t.id);
      setTimeout(() => setCopiadoId((prev) => (prev === t.id ? null : prev)), 1500);
    } catch (err) {
      console.error('Error al copiar al portapapeles:', err);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative sm:col-span-1">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar por código o nombre..."
            className={`${inputClass} pl-9`}
          />
        </div>
        <select className={inputClass} value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}>
          <option value="">Todas las categorías</option>
          {CATEGORIAS_TRANSACCION.map((c) => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
        <select className={inputClass} value={filtroSolucion} onChange={(e) => setFiltroSolucion(e.target.value)}>
          <option value="">Todas las soluciones</option>
          {SOLUCIONES_TRANSACCION.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </div>

      {filtrados.length === 0 ? (
        <p className="text-sm text-gray-500 bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
          {transacciones.length === 0 ? 'Todavía no hay transacciones registradas en el catálogo.' : 'Ninguna transacción coincide con el filtro.'}
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtrados.map((t) => {
            const catMeta = categoriaTransaccionMeta(t.categoria);
            const solMeta = solucionTransaccionMeta(t.solucion);
            return (
              <div key={t.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col gap-3">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="text-sm font-mono font-semibold text-white leading-snug break-words">{t.transaccion}</h4>
                  <button
                    type="button"
                    onClick={() => handleCopiar(t)}
                    title="Copiar Transacción"
                    className="flex-shrink-0 p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                  >
                    {copiadoId === t.id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {esZ && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-amber-300 bg-amber-500/10 border-amber-500/30">
                      Z
                    </span>
                  )}
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-purple-300 bg-purple-500/10 border-purple-500/30">
                    {catMeta.label}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border text-indigo-300 bg-indigo-500/10 border-indigo-500/30">
                    {solMeta.label}
                  </span>
                </div>

                <p className="text-xs text-gray-300 font-medium leading-relaxed">{t.nombre}</p>

                {t.descripcion && t.descripcion !== t.nombre && (
                  <p className="text-xs text-gray-400 leading-relaxed">{t.descripcion}</p>
                )}

                {esZ && (t.responsable || (t.sistemasDisponibles && t.sistemasDisponibles.length > 0) || t.rolPermiso) && (
                  <div className="pt-2 border-t border-white/5 space-y-1.5">
                    {t.responsable && (
                      <p className="text-[11px] text-gray-500">Responsable: <span className="text-gray-300">{t.responsable}</span></p>
                    )}
                    {t.sistemasDisponibles && t.sistemasDisponibles.length > 0 && (
                      <div className="flex items-center gap-1">
                        {t.sistemasDisponibles.map((s) => (
                          <span key={s} className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border text-sky-300 bg-sky-500/10 border-sky-500/30">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                    {t.rolPermiso && (
                      <p className="text-[11px] text-gray-500">Rol/Permiso: <span className="text-gray-300 font-mono">{t.rolPermiso}</span></p>
                    )}
                  </div>
                )}

                {!readOnly && (onEdit || onDelete) && (
                  <div className="flex items-center justify-end gap-1.5 pt-2 mt-auto border-t border-white/5">
                    {onEdit && (
                      <button
                        type="button"
                        onClick={() => onEdit(t)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </button>
                    )}
                    {onDelete && (
                      <button
                        type="button"
                        onClick={() => onDelete(t)}
                        className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
